// This is 3/3 dialogs used by distribution locations to send their customers a
// order drop off email.   It displays the custom email message, the users and the greeting and
// asks the distribution location to confirm before sending out the emails.  It will
// then attach the order to each email.
import React, { useEffect, useState, useContext } from "react";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import useCommunityOrder from "../../../../functions/Firestore/useCommunityOrder";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import OrderDropOffMessage from "./functions/OrderDropOffMessage.js";
import FinalizeDeliveryFees from "./functions/FinalizeDeliveryFees.js";
import OrderDropOffEmails from "./functions/OrderDropOffEmails.js";
import SetBaseDeliveryFees from "./functions/SetBaseDeliveryFees.js";
import {
  ChangeOrderLogContext,
  CommunityOrderChunksContext,
} from "../../../../pages/MyAccount.js";
import batchEmails from "../../../../functions/Email/BatchEmailer.js";
import RefreshCommunityHubData from "../../../../pages/MyAccountPage/Functions/LoadingOrderHistory/RefreshCommunityHubData.js";

export default function ConfirmOrderDropOffMessageDialog({
  openConfirmOrderDropOffMessageDialog,
  setOpenConfirmOrderDropOffMessageDialog,
  stateOfDialog,
  setStateOfDialog,
  userEmails,
  checkList,
  setCheckList,
  greeting,
  message,
  userInfo,
  setSendingEmails,
  communityHubUsers,
  order,
  sendEmails,
  orderIndex,
}) {
  // This hook will store the user and their custom message {firstName-lastName-userId : customMessage + order}
  const [userDropOffMessages, setUserDropOffMessages] = useState({});

  // This contains the updatePaymentHistory so that when the user updates
  // the order it displays correctly on the myAccount Page
  // eslint-disable-next-line no-unused-vars
  const { orderLog, updateOrderLog, updatePaymentHistory } = useContext(
    ChangeOrderLogContext,
  );

  // Reload allows you to check and update the community order.
  const { reloadOrder } = useCommunityOrder(order.selectedDate);

  // communityOrderChunks are in {orderdate: [communityOrders, overFlowCommunityOrders1, etc.]}
  // eslint-disable-next-line no-unused-vars
  const { communityOrderChunks = [], setCommunityOrderChunks = () => {} } =
    useContext(CommunityOrderChunksContext) || {};

  // This is the base delivery fee that is charged for delivery.  The actual delivery
  // fee charged to each user is the base amount divided amongst the users.
  const { baseDeliveryFees, minDeliveryFees } = SetBaseDeliveryFees(
    communityHubUsers,
    userInfo.baseDeliveryFee,
  );

  // If the user hits the back button then it will clear their checkList and set the
  // the current state (value) to "Custom" where they will write their message and
  // choose who to email.
  const handleBack = (value) => {
    setOpenConfirmOrderDropOffMessageDialog(false);
    setStateOfDialog(value);
    setCheckList({});
  };
  // If the user close the dialog then reset the state of this dialog series to
  // "selectAction" and clear the checkList.
  const handleClose = () => {
    setOpenConfirmOrderDropOffMessageDialog(false);
    setCheckList({});
    setStateOfDialog("selectAction");
  };

  // Since we have the imported orders the userEmails is a dictionary like this
  // {location : {user} : email}.  We don't need the location on this dialog so this
  // is a dictionary with just the {user : email}
  const userEmailsNoLocation = {};

  // Cycle through the userEmails to remove the locations and just have the {user : email}
  Object.keys(userEmails).forEach((location) => {
    Object.keys(userEmails[location]).forEach((user) => {
      userEmailsNoLocation[user] = userEmails[location][user];
    });
  });

  // If the user confirms they wish to send the emails.
  const handleConfirm = async () => {
    // Creates the batch of emails to send off.
    const emailBatch = [];

    // reloadOrder now returns { order, communityOrderChunks }
    const { order: latestOrder, communityOrderChunks: latestChunks } =
      await reloadOrder();

    // Since we are getting back the data we want to update communityHubUsers
    // and the delivery fees.  We don't need to update the hooks so we can just
    // use null on those.
    const {
      communityHubUsers: latestHubUsers,
      baseDeliveryFees: latestBaseDeliveryFees,
      minDeliveryFees: latestMinDeliveryFees,
    } = await RefreshCommunityHubData(
      latestOrder,
      null,
      latestChunks,
      setCommunityOrderChunks,
      null,
      null,
      null,
      null,
      null,
    );

    // Create an object of the userDropOffMessages to cycle through.
    const users = Object.keys(userDropOffMessages);

    // If the user wants to send the users emails we will do that otherwise we will just finalize
    // the delivery fees.
    if (sendEmails) {
      // Cycle through the user's with a drop off message and all the
      // parameters for each email. Push them to an array to send off the emails.
      for (const user of users) {
        emailBatch.push([
          userEmailsNoLocation,
          user,
          setSendingEmails,
          greeting,
          userDropOffMessages,
        ]);
      }
      // Call the batch email function that will handle the delays necessary
      // to sending the emails off without overloading the send mail function.
      await batchEmails({
        batchedParamsForEmailFunc: emailBatch,
        singularEmailFunc: OrderDropOffEmails,
      });

      // Close the dialog
      setOpenConfirmOrderDropOffMessageDialog(false);
      // Reset the checklist.
      setCheckList({});
      // reset the state.
      setStateOfDialog("selectAction");
      // Close the sending emails progress dialog
      setSendingEmails(false);
      // Add the delivery fees to the users.
      FinalizeDeliveryFees(
        latestHubUsers,
        checkList,
        latestOrder,
        updatePaymentHistory,
        latestBaseDeliveryFees,
        latestMinDeliveryFees,
        latestChunks,
        setCommunityOrderChunks,
        orderLog,
        updateOrderLog,
        orderIndex,
      );
    } else {
      // Close the dialog
      setOpenConfirmOrderDropOffMessageDialog(false);
      // Reset the checklist.
      setCheckList({});
      // reset the state.
      setStateOfDialog("selectAction");
      // Close the sending emails progress dialog
      setSendingEmails(false);
      // Add the delivery fees to the users.
      FinalizeDeliveryFees(
        latestHubUsers,
        checkList,
        latestOrder,
        updatePaymentHistory,
        latestBaseDeliveryFees,
        latestMinDeliveryFees,
        latestChunks,
        setCommunityOrderChunks,
        orderLog,
        updateOrderLog,
        orderIndex,
      );
    }
  };

  // When the state of the dialog is set to ConfirmOrderDropOff then we will
  // create the new order drop off message with the custom message included.
  useEffect(() => {
    if (stateOfDialog === "ConfirmOrderDropOff") {
      OrderDropOffMessage(
        message,
        communityHubUsers,
        checkList,
        setUserDropOffMessages,
        baseDeliveryFees,
        minDeliveryFees,
      );
    }
  }, [stateOfDialog]);

  return (
    <div>
      <Dialog
        onClose={handleClose}
        aria-labelledby="simple-dialog-title"
        open={openConfirmOrderDropOffMessageDialog}
      >
        <DialogTitle id="simple-dialog-title">Confirm Emails</DialogTitle>
        <DialogContent>
          <List>
            <h6>
              <u>Users being emailed</u>
            </h6>
            {Object.keys(checkList).map((user, i) => (
              <span key={i}>
                {checkList[user] && (
                  <ListItem autoFocus>
                    <ListItemText
                      primary={[
                        user.split("-")[0],
                        user.split("-")[1],
                        ":",
                        userEmailsNoLocation[user],
                      ].join(" ")}
                    />
                  </ListItem>
                )}
              </span>
            ))}
          </List>

          <h6>
            <u>Custom Message</u>
          </h6>
          <p>
            {greeting} "First Name",
            <br />
            {message}
          </p>
        </DialogContent>
        <DialogActions>
          <Button color="primary" onClick={() => handleBack("OrderDropOff")}>
            BACK
          </Button>
          {sendEmails ? (
            <Button color="primary" onClick={() => handleConfirm()}>
              SEND
            </Button>
          ) : (
            <Button color="primary" onClick={() => handleConfirm()}>
              CONFIRM
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </div>
  );
}
